/**
 * Google API Service for fetching travel data
 * This service handles Google Places, Flights, and other travel-related APIs
 */

class GoogleAPIService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    this.isConfigured = !!this.apiKey;
  }

  /**
   * Extract airport code from location string
   */
  extractAirportCode(location) {
    if (location.includes('San Francisco')) return 'SFO';
    if (location.includes('New York')) return 'JFK';
    if (location.includes('London')) return 'LHR';
    if (location.includes('Madinah') || location.includes('Medina')) return 'MED';
    if (location.includes('Dubai')) return 'DXB';
    if (location.includes('Jeddah')) return 'JED';
    if (location.includes('Riyadh')) return 'RUH';
    if (location.includes('Paris')) return 'CDG';
    if (location.includes('Tokyo')) return 'NRT';
    if (location.includes('Sydney')) return 'SYD';
    // Default fallback
    return location.split(',')[0].substring(0, 3).toUpperCase();
  }

  /**
   * Extract city name from location string
   */
  extractCityName(location) {
    if (location.includes('Madinah') || location.includes('Medina')) return 'Madinah';
    if (location.includes('San Francisco')) return 'San Francisco';
    if (location.includes('New York')) return 'New York';
    if (location.includes('London')) return 'London';
    if (location.includes('Dubai')) return 'Dubai';
    if (location.includes('Jeddah')) return 'Jeddah';
    if (location.includes('Riyadh')) return 'Riyadh';
    if (location.includes('Paris')) return 'Paris';
    if (location.includes('Tokyo')) return 'Tokyo';
    if (location.includes('Sydney')) return 'Sydney';
    // Default fallback - take first part before comma
    return location.split(',')[0];
  }

  /**
   * Calculate number of nights between dates
   */
  calculateNights(checkInDate, checkOutDate) {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const diffTime = checkOut - checkIn;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays);
  }

  /**
   * Fetch flights data using Google Flights API
   * Note: Google Flights API is not publicly available, so this is a placeholder
   */
  async fetchFlights(fromLocation, toLocation, departureDate, returnDate, preferences = {}) {
    if (!this.isConfigured) {
      throw new Error('Google API key not configured');
    }

    console.log('✈️ Flight search preferences:', preferences);

    // Mock data based on actual search parameters
    const fromCode = this.extractAirportCode(fromLocation);
    const toCode = this.extractAirportCode(toLocation);
    const passengers = preferences.travelers || 1;
    const budget = preferences.budget;
    const transportPrefs = preferences.transportPreferences || [];
    const flexibleDates = preferences.flexibleDates;
    
    // Adjust flight options based on preferences
    const basePrice = budget ? Math.min(budget * 0.4, 2000) : 1500; // 40% of budget for flights
    const prefersDirect = transportPrefs.includes('flight') && !transportPrefs.includes('train');
    
    const flights = [
      {
        airline: 'Saudi Airlines',
        departure: fromCode,
        arrival: toCode,
        departureTime: '14:30',
        arrivalTime: '12:45+1',
        duration: '16h 15m',
        price: `$${Math.floor(basePrice)}`,
        stops: '1 stop in Jeddah',
        passengers: passengers,
        date: departureDate,
        direct: false,
        flexible: flexibleDates
      }
    ];

    // Add direct flight if user prefers direct or has higher budget
    if (prefersDirect || budget > 2000) {
      flights.push({
        airline: 'Saudi Airlines',
        departure: fromCode,
        arrival: toCode,
        departureTime: '08:00',
        arrivalTime: '06:30+1',
        duration: '14h 30m',
        price: `$${Math.floor(basePrice * 1.5)}`,
        stops: 'Direct',
        passengers: passengers,
        date: departureDate,
        direct: true,
        flexible: flexibleDates
      });
    }

    // Add Emirates option
    flights.push({
      airline: 'Emirates',
      departure: fromCode,
      arrival: toCode,
      departureTime: '22:15',
      arrivalTime: '18:30+1',
      duration: '18h 15m',
      price: `$${Math.floor(basePrice * 1.2)}`,
      stops: '1 stop in Dubai',
      passengers: passengers,
      date: departureDate,
      direct: false,
      flexible: flexibleDates
    });

    // Add Turkish Airlines option
    flights.push({
      airline: 'Turkish Airlines',
      departure: fromCode,
      arrival: toCode,
      departureTime: '20:45',
      arrivalTime: '16:20+1',
      duration: '17h 35m',
      price: `$${Math.floor(basePrice * 1.1)}`,
      stops: '1 stop in Istanbul',
      passengers: passengers,
      date: departureDate,
      direct: false,
      flexible: flexibleDates
    });

    // Add Qatar Airways option
    flights.push({
      airline: 'Qatar Airways',
      departure: fromCode,
      arrival: toCode,
      departureTime: '23:30',
      arrivalTime: '19:15+1',
      duration: '15h 45m',
      price: `$${Math.floor(basePrice * 1.3)}`,
      stops: '1 stop in Doha',
      passengers: passengers,
      date: departureDate,
      direct: false,
      flexible: flexibleDates
    });

    return flights;
  }

  /**
   * Fetch hotels using Google Places API
   */
  async fetchHotels(location, checkInDate, checkOutDate, preferences = {}) {
    if (!this.isConfigured) {
      throw new Error('Google API key not configured');
    }

    console.log('🏨 Hotel search preferences:', preferences);

    // Mock data based on actual search parameters
    const cityName = this.extractCityName(location);
    const nights = this.calculateNights(checkInDate, checkOutDate);
    const guests = preferences.travelers || 1;
    const budget = preferences.budget;
    const budgetType = preferences.budgetType || 'per_person';
    const accommodationStyles = preferences.accommodationStyles || [];
    // const priceRange = preferences.priceRange; // Will be used when implementing actual Google Places API
    
    // Calculate budget per night
    let basePrice;
    if (budget) {
      if (budgetType === 'per_person') {
        basePrice = Math.min(budget / nights, 200); // Per person per night
      } else {
        basePrice = Math.min(budget / nights / guests, 200); // Total budget divided by nights and guests
      }
    } else {
      basePrice = 150; // Default price
    }

    // Adjust based on accommodation style preferences
    const prefersLuxury = accommodationStyles.includes('resort') || accommodationStyles.includes('luxury');
    const prefersBudget = accommodationStyles.includes('hostel') || accommodationStyles.includes('budget');
    const prefersBusiness = accommodationStyles.includes('business') || accommodationStyles.includes('hotel');

    const hotels = [];

    // Add luxury option if user prefers luxury or has high budget
    if (prefersLuxury || budget > 2000) {
      hotels.push({
        name: `${cityName} Hilton`,
        rating: 4.5,
        price: `$${Math.floor(basePrice * 1.5)}/night`,
        address: `Near city center, ${cityName}`,
        amenities: ['WiFi', 'Pool', 'Restaurant', 'Prayer Room', 'Spa', 'Concierge'],
        checkIn: checkInDate,
        checkOut: checkOutDate,
        guests: guests,
        nights: nights,
        totalPrice: `$${Math.floor(basePrice * 1.5 * nights)}`,
        style: 'Luxury',
        category: 'resort'
      });
    }

    // Add business hotel
    if (prefersBusiness || !prefersBudget) {
      hotels.push({
        name: `Dar Al Hijra InterContinental`,
        rating: 4.3,
        price: `$${Math.floor(basePrice * 1.2)}/night`,
        address: `King Fahd Road, ${cityName}`,
        amenities: ['WiFi', 'Gym', 'Restaurant', 'Airport Shuttle', 'Business Center'],
        checkIn: checkInDate,
        checkOut: checkOutDate,
        guests: guests,
        nights: nights,
        totalPrice: `$${Math.floor(basePrice * 1.2 * nights)}`,
        style: 'Business',
        category: 'hotel'
      });
    }

    // Add budget option if user prefers budget or has lower budget
    if (prefersBudget || budget < 1000) {
      hotels.push({
        name: `${cityName} Budget Inn`,
        rating: 3.8,
        price: `$${Math.floor(basePrice * 0.7)}/night`,
        address: `Downtown ${cityName}`,
        amenities: ['WiFi', 'Basic Breakfast'],
        checkIn: checkInDate,
        checkOut: checkOutDate,
        guests: guests,
        nights: nights,
        totalPrice: `$${Math.floor(basePrice * 0.7 * nights)}`,
        style: 'Budget',
        category: 'hostel'
      });
    }

    // Always add a mid-range option
    hotels.push({
      name: `${cityName} Marriott`,
      rating: 4.4,
      price: `$${Math.floor(basePrice)}/night`,
      address: `King Abdulaziz Road, ${cityName}`,
      amenities: ['WiFi', 'Pool', 'Spa', 'Business Center', 'Restaurant'],
      checkIn: checkInDate,
      checkOut: checkOutDate,
      guests: guests,
      nights: nights,
      totalPrice: `$${Math.floor(basePrice * nights)}`,
      style: 'Mid-range',
      category: 'hotel'
    });

    // Add more hotel options
    hotels.push({
      name: `${cityName} Holiday Inn`,
      rating: 4.0,
      price: `$${Math.floor(basePrice * 0.9)}/night`,
      address: `Airport Road, ${cityName}`,
      amenities: ['WiFi', 'Airport Shuttle', 'Restaurant', 'Gym'],
      checkIn: checkInDate,
      checkOut: checkOutDate,
      guests: guests,
      nights: nights,
      totalPrice: `$${Math.floor(basePrice * 0.9 * nights)}`,
      style: 'Mid-range',
      category: 'hotel'
    });

    hotels.push({
      name: `${cityName} Sheraton`,
      rating: 4.6,
      price: `$${Math.floor(basePrice * 1.4)}/night`,
      address: `City Center, ${cityName}`,
      amenities: ['WiFi', 'Pool', 'Spa', 'Multiple Restaurants', 'Concierge'],
      checkIn: checkInDate,
      checkOut: checkOutDate,
      guests: guests,
      nights: nights,
      totalPrice: `$${Math.floor(basePrice * 1.4 * nights)}`,
      style: 'Luxury',
      category: 'resort'
    });

    return hotels;
  }

  /**
   * Fetch restaurants using Google Places API
   */
  async fetchRestaurants(location, preferences = {}) {
    if (!this.isConfigured) {
      throw new Error('Google API key not configured');
    }

    console.log('🍽️ Restaurant search preferences:', preferences);

    // Mock data based on actual search parameters
    const cityName = this.extractCityName(location);
    const cuisineTypes = preferences.cuisineTypes || ['Local'];
    const diningStyle = preferences.diningStyle || [];
    const dietaryRestrictions = preferences.dietaryRestrictions;
    const priceRange = preferences.priceRange;
    const budget = preferences.budget;
    // const specialRequests = preferences.specialRequests; // Will be used when implementing actual Google Places API
    
    // Calculate price range based on budget
    let priceLevel = priceRange;
    if (!priceLevel && budget) {
      const dailyBudget = budget / 3; // Assume 3 days
      if (dailyBudget > 100) priceLevel = '$$$';
      else if (dailyBudget > 50) priceLevel = '$$';
      else priceLevel = '$';
    }

    const restaurants = [];

    // Add restaurants based on cuisine preferences
    cuisineTypes.forEach(cuisine => {
      if (cuisine === 'Middle Eastern' || cuisine === 'Local') {
        restaurants.push({
          name: `Al Tazaj`,
          rating: 4.4,
          price: priceLevel || '$$',
          address: `Prince Mohammed Bin Abdulaziz Road, ${cityName}`,
          cuisine: 'Middle Eastern',
          specialties: ['Grilled Chicken', 'Rice Dishes', 'Hummus'],
          priceRange: priceLevel,
          dietaryFriendly: dietaryRestrictions?.toLowerCase().includes('halal') ? 'Halal' : 'Standard',
          style: diningStyle.includes('casual') ? 'Casual' : 'Traditional'
        });
      }

      if (cuisine === 'Fast Food' || cuisine === 'American') {
        restaurants.push({
          name: `Al Baik`,
          rating: 4.2,
          price: priceLevel || '$$',
          address: `King Fahd Road, ${cityName}`,
          cuisine: 'Fast Food',
          specialties: ['Fried Chicken', 'Hamburgers', 'Fries'],
          priceRange: priceLevel,
          dietaryFriendly: dietaryRestrictions?.toLowerCase().includes('halal') ? 'Halal' : 'Standard',
          style: 'Fast Casual'
        });
      }

      if (cuisine === 'Italian' || cuisine === 'European') {
        restaurants.push({
          name: `${cityName} Italian Bistro`,
          rating: 4.3,
          price: priceLevel || '$$$',
          address: `Downtown ${cityName}`,
          cuisine: 'Italian',
          specialties: ['Pasta', 'Pizza', 'Gelato'],
          priceRange: priceLevel,
          dietaryFriendly: dietaryRestrictions?.toLowerCase().includes('vegetarian') ? 'Vegetarian Options' : 'Standard',
          style: diningStyle.includes('fine_dining') ? 'Fine Dining' : 'Casual'
        });
      }
    });

    // Add local specialty if no specific cuisine preferences
    if (cuisineTypes.length === 0 || cuisineTypes.includes('Local')) {
      restaurants.push({
        name: `Kudu`,
        rating: 4.1,
        price: priceLevel || '$$',
        address: `King Abdulaziz Road, ${cityName}`,
        cuisine: 'Local',
        specialties: ['Sandwiches', 'Fresh Juices', 'Local Coffee'],
        priceRange: priceLevel,
        dietaryFriendly: dietaryRestrictions?.toLowerCase().includes('halal') ? 'Halal' : 'Standard',
        style: 'Casual'
      });
    }

    // Add more restaurant options
    restaurants.push({
      name: `${cityName} Steakhouse`,
      rating: 4.5,
      price: priceLevel || '$$$',
      address: `Downtown ${cityName}`,
      cuisine: 'Steakhouse',
      specialties: ['Premium Steaks', 'Wine Selection', 'Fine Dining'],
      priceRange: priceLevel,
      dietaryFriendly: 'Standard',
      style: 'Fine Dining'
    });

    restaurants.push({
      name: `${cityName} Seafood Market`,
      rating: 4.2,
      price: priceLevel || '$$',
      address: `Waterfront District, ${cityName}`,
      cuisine: 'Seafood',
      specialties: ['Fresh Fish', 'Lobster', 'Crab'],
      priceRange: priceLevel,
      dietaryFriendly: dietaryRestrictions?.toLowerCase().includes('halal') ? 'Halal' : 'Standard',
      style: 'Casual'
    });

    restaurants.push({
      name: `${cityName} Cafe`,
      rating: 4.0,
      price: priceLevel || '$',
      address: `Main Street, ${cityName}`,
      cuisine: 'Cafe',
      specialties: ['Coffee', 'Pastries', 'Light Meals'],
      priceRange: priceLevel,
      dietaryFriendly: dietaryRestrictions?.toLowerCase().includes('halal') ? 'Halal' : 'Standard',
      style: 'Casual'
    });

    // Filter by dietary restrictions
    if (dietaryRestrictions) {
      return restaurants.filter(restaurant => 
        restaurant.dietaryFriendly.toLowerCase().includes(dietaryRestrictions.toLowerCase()) ||
        restaurant.dietaryFriendly === 'Standard'
      );
    }

    return restaurants;
  }

  /**
   * Fetch attractions using Google Places API
   */
  async fetchAttractions(location, preferences = {}) {
    if (!this.isConfigured) {
      throw new Error('Google API key not configured');
    }

    console.log('🎯 Attraction search preferences:', preferences);

    // Mock data based on actual search parameters
    const cityName = this.extractCityName(location);
    const interests = preferences.interests || [];
    const tripTypes = preferences.tripTypes || [];
    const preferredPace = preferences.preferredPace || 'balanced';
    const mustSeeItems = preferences.mustSeeItems;
    
    const attractions = [];

    // Religious attractions (always include for Madinah)
    if (cityName.toLowerCase().includes('madinah') || cityName.toLowerCase().includes('medina')) {
      attractions.push({
        name: `Prophet's Mosque`,
        rating: 4.9,
        price: 'Free',
        address: `Al Haram, ${cityName}`,
        type: 'Religious',
        description: 'The second holiest site in Islam',
        hours: '24/7',
        category: 'Religious',
        pace: 'relaxed',
        duration: '2-4 hours'
      });

      attractions.push({
        name: `Quba Mosque`,
        rating: 4.8,
        price: 'Free',
        address: `Quba, ${cityName}`,
        type: 'Religious',
        description: 'The first mosque built by Prophet Muhammad',
        hours: '24/7',
        category: 'Religious',
        pace: 'relaxed',
        duration: '1-2 hours'
      });
    }

    // Cultural attractions based on interests
    if (interests.includes('museums') || interests.includes('culture') || tripTypes.includes('cultural')) {
      attractions.push({
        name: `${cityName} National Museum`,
        rating: 4.3,
        price: '$5-10',
        address: `King Abdulaziz Road, ${cityName}`,
        type: 'Cultural',
        description: 'Learn about local history and culture',
        hours: '9:00 AM - 5:00 PM',
        category: 'Cultural',
        pace: preferredPace,
        duration: '2-3 hours'
      });
    }

    // Nature attractions
    if (interests.includes('nature') || interests.includes('outdoor') || tripTypes.includes('adventure')) {
      attractions.push({
        name: `${cityName} Desert Safari`,
        rating: 4.5,
        price: '$50-100',
        address: `Desert outskirts, ${cityName}`,
        type: 'Adventure',
        description: 'Experience the desert landscape and traditional activities',
        hours: 'Sunset tours available',
        category: 'Adventure',
        pace: 'active',
        duration: '4-6 hours'
      });
    }

    // Shopping attractions
    if (interests.includes('shopping') || tripTypes.includes('relaxation')) {
      attractions.push({
        name: `${cityName} Souk`,
        rating: 4.2,
        price: 'Free entry',
        address: `Traditional market, ${cityName}`,
        type: 'Shopping',
        description: 'Traditional market with local crafts and souvenirs',
        hours: '9:00 AM - 10:00 PM',
        category: 'Shopping',
        pace: 'relaxed',
        duration: '1-3 hours'
      });
    }

    // Historical attractions
    if (interests.includes('history') || interests.includes('heritage')) {
      attractions.push({
        name: `${cityName} Heritage Village`,
        rating: 4.4,
        price: '$10-15',
        address: `Heritage district, ${cityName}`,
        type: 'Historical',
        description: 'Preserved historical buildings and traditional architecture',
        hours: '10:00 AM - 6:00 PM',
        category: 'Historical',
        pace: preferredPace,
        duration: '2-4 hours'
      });
    }

    // Add more attraction options
    attractions.push({
      name: `${cityName} Shopping Mall`,
      rating: 4.1,
      price: 'Free entry',
      address: `Commercial District, ${cityName}`,
      type: 'Shopping',
      description: 'Modern shopping center with international brands',
      hours: '10:00 AM - 10:00 PM',
      category: 'Shopping',
      pace: 'relaxed',
      duration: '2-3 hours'
    });

    attractions.push({
      name: `${cityName} Park`,
      rating: 4.3,
      price: 'Free',
      address: `City Center, ${cityName}`,
      type: 'Nature',
      description: 'Beautiful park with gardens and walking paths',
      hours: '6:00 AM - 10:00 PM',
      category: 'Nature',
      pace: 'relaxed',
      duration: '1-2 hours'
    });

    attractions.push({
      name: `${cityName} Cultural Center`,
      rating: 4.2,
      price: '$5-10',
      address: `Arts District, ${cityName}`,
      type: 'Cultural',
      description: 'Art galleries, theaters, and cultural exhibitions',
      hours: '9:00 AM - 8:00 PM',
      category: 'Cultural',
      pace: preferredPace,
      duration: '2-3 hours'
    });

    // Filter by must-see items if specified
    if (mustSeeItems) {
      const mustSeeLower = mustSeeItems.toLowerCase();
      return attractions.filter(attraction => 
        attraction.name.toLowerCase().includes(mustSeeLower) ||
        attraction.description.toLowerCase().includes(mustSeeLower) ||
        attraction.type.toLowerCase().includes(mustSeeLower)
      );
    }

    // Adjust based on preferred pace
    if (preferredPace === 'relaxed') {
      return attractions.filter(attraction => 
        attraction.pace === 'relaxed' || attraction.duration.includes('1-2') || attraction.duration.includes('2-3')
      );
    } else if (preferredPace === 'fast') {
      return attractions.filter(attraction => 
        attraction.pace === 'active' || attraction.duration.includes('1-2')
      );
    }

    return attractions;
  }

  /**
   * Fetch comprehensive travel data for a trip
   */
  async fetchTravelData(fromLocation, toLocation, startDate, endDate, preferences = {}) {
    try {
      console.log('🔍 Google API - Fetching travel data with parameters:', {
        fromLocation,
        toLocation,
        startDate,
        endDate,
        travelers: preferences.travelers || preferences.adults || 1,
        budget: preferences.budget,
        cuisineTypes: preferences.cuisineTypes,
        interests: preferences.interests
      });

      // Extract specific preferences for each category
      const flightPrefs = {
        travelers: preferences.travelers || preferences.adults || 1,
        budget: preferences.budget,
        transportPreferences: preferences.transportPreferences,
        flexibleDates: preferences.flexibleDates
      };

      const hotelPrefs = {
        travelers: preferences.travelers || preferences.adults || 1,
        budget: preferences.budget,
        budgetType: preferences.budgetType,
        accommodationStyles: preferences.accommodationStyles,
        priceRange: preferences.priceRange
      };

      const restaurantPrefs = {
        cuisineTypes: preferences.cuisineTypes,
        diningStyle: preferences.diningStyle,
        dietaryRestrictions: preferences.dietaryRestrictions,
        priceRange: preferences.priceRange,
        budget: preferences.budget,
        specialRequests: preferences.specialRequests
      };

      const attractionPrefs = {
        interests: preferences.interests,
        tripTypes: preferences.tripTypes,
        preferredPace: preferences.preferredPace,
        mustSeeItems: preferences.mustSeeItems
      };

      const [flights, hotels, restaurants, attractions] = await Promise.all([
        this.fetchFlights(fromLocation, toLocation, startDate, endDate, flightPrefs),
        this.fetchHotels(toLocation, startDate, endDate, hotelPrefs),
        this.fetchRestaurants(toLocation, restaurantPrefs),
        this.fetchAttractions(toLocation, attractionPrefs)
      ]);

      console.log('📊 Google API - Results:', {
        flights: flights.length,
        hotels: hotels.length,
        restaurants: restaurants.length,
        attractions: attractions.length
      });

      return {
        flights,
        hotels,
        restaurants,
        attractions
      };
    } catch (error) {
      console.error('Error fetching travel data:', error);
      throw error;
    }
  }
}

const googleAPIService = new GoogleAPIService();
export default googleAPIService;
